/**
 * Glicko-2 Rating System for Padel Brothers
 * 
 * Constants:
 * - Default Rating: 1500 (equivalent to 1000 ELO)
 * - Default RD: 350 (new player, high uncertainty)
 * - Default Volatility: 0.06
 * - System constant τ: 0.5 (controls volatility change)
 */

import i18n from '../i18n';

// === CONSTANTS ===
export const DEFAULT_RATING = 1500;
export const DEFAULT_RD = 350;
export const DEFAULT_VOLATILITY = 0.06;
export const MIN_RATING = 150;
export const MAX_RATING = 4500;
export const MIN_RD = 30;
export const MAX_RD = 350;
export const TAU = 0.5; // System constant
export const CONVERGENCE_TOLERANCE = 0.000001;

// RD decay: how much RD increases per day of inactivity
export const RD_DECAY_PER_DAY = 1.5;
export const MAX_INACTIVITY_DAYS = 180; // After 6 months, RD resets to max

// === INTERFACES ===
export interface Glicko2Rating {
    rating: number;
    rd: number;
    volatility: number;
}

export interface Glicko2Result {
    opponentRating: number;
    opponentRd: number;
    score: number; // 1 = win, 0.5 = draw, 0 = loss
    weight?: number; // Fraction of match (1.0 = standard, 0.25 = long Americano, 0.10 = Super Tie-Break)
}

// RatingHistory is defined in types/user.ts

// === CONVERSION FUNCTIONS ===

/**
 * Convert ELO to Glicko-2 rating
 * ELO 1000 → Rating 1500
 */
export const convertEloToRating = (elo: number): number => {
    return Math.round(elo * 1.5);
};

/**
 * Convert Glicko-2 rating to ELO (for display compatibility)
 * Rating 1500 → ELO 1000
 */
export const convertRatingToElo = (rating: number): number => {
    return Math.round(rating / 1.5);
};

// === GLICKO-2 MATH FUNCTIONS ===

/**
 * Convert rating to Glicko-2 scale (μ)
 */
const toGlicko2Scale = (rating: number): number => {
    return (rating - 1500) / 173.7178;
};

/**
 * Convert back from Glicko-2 scale
 */
const fromGlicko2Scale = (mu: number): number => {
    return mu * 173.7178 + 1500;
};

/**
 * Convert RD to Glicko-2 scale (φ)
 */
const rdToGlicko2Scale = (rd: number): number => {
    return rd / 173.7178;
};

/**
 * Convert back from Glicko-2 scale
 */
const rdFromGlicko2Scale = (phi: number): number => {
    return phi * 173.7178;
};

/**
 * g(φ) function
 */
const g = (phi: number): number => {
    return 1 / Math.sqrt(1 + 3 * phi * phi / (Math.PI * Math.PI));
};

/**
 * E(μ, μj, φj) function - expected score
 */
const E = (mu: number, muj: number, phij: number): number => {
    return 1 / (1 + Math.exp(-g(phij) * (mu - muj)));
};

/**
 * Calculate variance v
 */
const calculateVariance = (mu: number, results: Glicko2Result[]): number => {
    let sum = 0;
    for (const result of results) {
        const muj = toGlicko2Scale(result.opponentRating);
        const phij = rdToGlicko2Scale(result.opponentRd);
        const gPhij = g(phij);
        const e = E(mu, muj, phij);
        const weight = result.weight ?? 1.0;
        sum += weight * (gPhij * gPhij * e * (1 - e));
    }
    return sum > 0 ? 1 / sum : 1000000; // Avoid division by zero
};

/**
 * Calculate delta (improvement)
 */
const calculateDelta = (mu: number, v: number, results: Glicko2Result[]): number => {
    let sum = 0;
    for (const result of results) {
        const muj = toGlicko2Scale(result.opponentRating);
        const phij = rdToGlicko2Scale(result.opponentRd);
        const weight = result.weight ?? 1.0;
        sum += weight * (g(phij) * (result.score - E(mu, muj, phij)));
    }
    return v * sum;
};

/**
 * Calculate new volatility using Illinois algorithm
 */
const calculateNewVolatility = (
    phi: number,
    v: number,
    delta: number,
    sigma: number
): number => {
    const a = Math.log(sigma * sigma);
    const phiSquared = phi * phi;
    const deltaSquared = delta * delta;

    const f = (x: number): number => {
        const ex = Math.exp(x);
        const top = ex * (deltaSquared - phiSquared - v - ex);
        const bottom = 2 * Math.pow(phiSquared + v + ex, 2);
        return top / bottom - (x - a) / (TAU * TAU);
    };

    // Find initial bounds
    let A = a;
    let B: number;

    if (deltaSquared > phiSquared + v) {
        B = Math.log(deltaSquared - phiSquared - v);
    } else {
        let k = 1;
        while (f(a - k * TAU) < 0) {
            k++;
            if (k > 100) break; // Safety limit
        }
        B = a - k * TAU;
    }

    // Illinois algorithm
    let fA = f(A);
    let fB = f(B);
    let iterations = 0;

    while (Math.abs(B - A) > CONVERGENCE_TOLERANCE && iterations < 100) {
        const C = A + (A - B) * fA / (fB - fA);
        const fC = f(C);

        if (fC * fB <= 0) {
            A = B;
            fA = fB;
        } else {
            fA = fA / 2;
        }

        B = C;
        fB = fC;
        iterations++;
    }

    return Math.exp(A / 2);
};

// === MAIN CALCULATION FUNCTION ===

/**
 * Calculate new Glicko-2 rating after a rating period (match/series of matches)
 * 
 * @param player Current player rating
 * @param results Array of match results in this period
 * @returns New rating, RD, and volatility
 */
export const calculateGlicko2 = (
    player: Glicko2Rating,
    results: Glicko2Result[]
): Glicko2Rating => {
    // If no games, increase RD (rating decay)
    if (results.length === 0) {
        const newPhi = Math.sqrt(
            rdToGlicko2Scale(player.rd) ** 2 + player.volatility ** 2
        );
        return {
            rating: player.rating,
            rd: Math.min(MAX_RD, rdFromGlicko2Scale(newPhi)),
            volatility: player.volatility
        };
    }

    // Step 1: Convert to Glicko-2 scale
    const mu = toGlicko2Scale(player.rating);
    const phi = rdToGlicko2Scale(player.rd);

    // Step 2: Calculate variance
    const v = calculateVariance(mu, results);

    // Step 3: Calculate delta
    const delta = calculateDelta(mu, v, results);

    // Step 4: Calculate new volatility
    const newSigma = calculateNewVolatility(phi, v, delta, player.volatility);

    // Step 5: Update to new pre-rating period values
    const phiStar = Math.sqrt(phi * phi + newSigma * newSigma);

    // Step 6: Update rating and RD
    const newPhi = 1 / Math.sqrt(1 / (phiStar * phiStar) + 1 / v);
    const newMu = mu + newPhi * newPhi * calculateDelta(mu, v, results) / v;

    // Convert back
    return {
        rating: clampRating(fromGlicko2Scale(newMu)),
        rd: clampRD(rdFromGlicko2Scale(newPhi)),
        volatility: newSigma
    };
};

// === UTILITY FUNCTIONS ===

/**
 * Clamp rating to valid range
 */
export const clampRating = (rating: number): number => {
    return Math.max(MIN_RATING, Math.min(MAX_RATING, rating));
};

/**
 * Clamp RD to valid range
 */
export const clampRD = (rd: number): number => {
    return Math.max(MIN_RD, Math.min(MAX_RD, rd));
};

/**
 * Get conservative rating (rating - 2*RD)
 * Used for rankings - accounts for uncertainty
 */
export const getConservativeRating = (rating: number, rd: number): number => {
    return Math.round(rating - 2 * rd);
};

/**
 * Get weighted team rating for doubles
 * Weaker player counts for 60% (they're targeted more)
 */
export const getWeightedTeamRating = (
    p1: Glicko2Rating,
    p2: Glicko2Rating
): Glicko2Rating => {
    const weaker = p1.rating < p2.rating ? p1 : p2;
    const stronger = p1.rating < p2.rating ? p2 : p1;

    return {
        rating: stronger.rating * 0.4 + weaker.rating * 0.6,
        rd: Math.sqrt((p1.rd * p1.rd + p2.rd * p2.rd) / 2), // Combine RD
        volatility: (p1.volatility + p2.volatility) / 2
    };
};

/**
 * Decay RD based on inactivity
 * RD increases over time without matches
 */
export const getDecayedRD = (
    currentRD: number,
    lastUpdateDate: string | undefined
): number => {
    if (!lastUpdateDate) return DEFAULT_RD;

    const daysSinceUpdate = Math.floor(
        (Date.now() - new Date(lastUpdateDate).getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceUpdate <= 0) return currentRD;
    if (daysSinceUpdate >= MAX_INACTIVITY_DAYS) return MAX_RD;

    return clampRD(currentRD + daysSinceUpdate * RD_DECAY_PER_DAY);
};

/**
 * Get reliability indicator based on RD
 * Lower RD = more reliable rating
 */
export const getReliabilityIndicator = (rd: number): 'high' | 'medium' | 'low' | 'new' => {
    if (rd <= 50) return 'high';
    if (rd <= 100) return 'medium';
    if (rd <= 200) return 'low';
    return 'new';
};

/**
 * Get RD display string (e.g., "±45")
 */
export const formatRDDisplay = (rd: number): string => {
    return `±${Math.round(rd)}`;
};

/**
 * Rating Status Definitions
 */
export type RatingStatus = 'novice' | 'validation' | 'confirmed' | 'inactive';

export const RD_THRESHOLDS = {
    NOVICE: 150,      // RD >= 150: New or very uncertain
    VALIDATION: 100,  // 100 <= RD < 150: Improving accuracy
    CONFIRMED: 100    // RD < 100: Reliable
};

/**
 * Get rating status based on RD and other factors
 * @param rd Rating Deviation
 * @param matchesPlayed Total matches played (optional, for heuristic)
 * @returns RatingStatus
 */
export const getRatingStatus = (rd: number, matchesPlayed: number = 0): RatingStatus => {
    // If RD is very high, it's either a new player or an inactive one returning
    if (rd >= RD_THRESHOLDS.NOVICE) {
        // If they have played many matches but have high RD, they are inactive/decayed
        if (matchesPlayed >= 10) return 'inactive';
        return 'novice';
    }

    // If RD is moderate
    if (rd >= RD_THRESHOLDS.VALIDATION) {
        return 'validation';
    }

    // Otherwise, confirmed
    return 'confirmed';
};

export const getRatingStatusLabel = (status: RatingStatus): string => {
    return i18n.t(`playerProfile.ratingStatus.${status}`);
};

export const getRatingStatusColor = (status: RatingStatus): string => {
    switch (status) {
        case 'novice': return 'text-red-400';
        case 'validation': return 'text-orange-400';
        case 'confirmed': return 'text-green-400';
        case 'inactive': return 'text-gray-400';
    }
};

export const getRatingStatusBg = (status: RatingStatus): string => {
    switch (status) {
        case 'novice': return 'bg-red-500/10 border-red-500/20';
        case 'validation': return 'bg-orange-500/10 border-orange-500/20';
        case 'confirmed': return 'bg-green-500/10 border-green-500/20';
        case 'inactive': return 'bg-gray-500/10 border-gray-500/20';
    }
};

/**
 * Calculate single match result using simplified Glicko-2
 * For real-time updates during play
 */
export const calculateSingleMatch = (
    winner: Glicko2Rating,
    loser: Glicko2Rating,
    isKingCourt: boolean = false,
    matchWeight: number = 1.0
): { winnerChange: number; loserChange: number; newWinnerRD: number; newLoserRD: number } => {
    const kingMultiplier = isKingCourt ? 1.2 : 1.0;

    // Calculate for winner
    const winnerResult = calculateGlicko2(winner, [{
        opponentRating: loser.rating,
        opponentRd: loser.rd,
        score: 1
    }]);

    // Calculate for loser
    const loserResult = calculateGlicko2(loser, [{
        opponentRating: winner.rating,
        opponentRd: winner.rd,
        score: 0
    }]);

    // Apply scaling
    const winnerChange = Math.round((winnerResult.rating - winner.rating) * kingMultiplier * matchWeight);
    const loserChange = Math.round((loserResult.rating - loser.rating) * matchWeight);

    return {
        winnerChange,
        loserChange,
        newWinnerRD: winnerResult.rd,
        newLoserRD: loserResult.rd
    };
};
